import {
  Flex,
  FormControl,
  FormControlProps,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Switch,
} from '@chakra-ui/react';
import { Controller, FieldPath, FieldPathValue, FieldValues, Validate, useFormContext } from 'react-hook-form';
import { useFormErrorMessage } from './useFormErrorMessage';

export interface FormSwitchProps<
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends FormControlProps {
  name: TFieldName;
  label: string;
  description?: React.ReactNode;
  validate?: Validate<FieldPathValue<TFieldValues, TFieldName>, TFieldValues>;
  singleLine?: boolean;
}

export function FormSwitch<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  description,
  isRequired,
  autoFocus,
  size,
  singleLine,
  ...rest
}: FormSwitchProps<TFieldValues>) {
  const {
    control,
    formState: { errors },
  } = useFormContext<TFieldValues>();

  const errorMessage = useFormErrorMessage(name, label, errors);

  return (
    <FormControl isRequired={isRequired} isInvalid={!!errors[name]} {...rest}>
      {singleLine ? (
        <Flex align="center" gap={2}>
          <Controller
            control={control}
            name={name}
            render={({ field }) => <Switch autoFocus={autoFocus} isChecked={field.value} size={size} {...field} />}
          />
          <FormLabel size={size} mb={0}>
            {label}
          </FormLabel>
        </Flex>
      ) : (
        <>
          <FormLabel size={size}>{label}</FormLabel>
          <Controller
            control={control}
            name={name}
            render={({ field }) => <Switch autoFocus={autoFocus} isChecked={field.value} size={size} {...field} />}
          />
        </>
      )}
      <FormErrorMessage>{errorMessage}</FormErrorMessage>
      <FormHelperText>{description}</FormHelperText>
    </FormControl>
  );
}
